/**
 * FIE Permission Matrix
 * Maps Koredor roles → FIE access levels
 */
import type { UserRole } from "../RoleContext";
import type { FIERole, FIEPermissions } from "./types";

const ROLE_TO_FIE_ROLE: Record<UserRole, FIERole | null> = {
  super_admin: "executive_finance",
  owner: "executive_finance",
  cfo: "executive_finance",
  finance_manager: "ops_finance",
  bookkeeper: "ops_finance",
  ops_manager: "limited_ops",
  admin: "limited_ops",
  warehouse_manager: null,
  head_of_installation: null,
  procurement_manager: null,
  inventory_manager: null,
  installer: null,
  subcontractor: null,
  sales: null,
  sales_manager: null,
  call_center: null,
  it_staff: "limited_ops",
};

const FIE_ROLE_PERMISSIONS: Record<FIERole, FIEPermissions> = {
  executive_finance: {
    viewAllPartnerProjections: true,
    viewOwnProjectionOnly: false,
    editAssumptions: true,
    editProtectedAssumptions: true,
    viewReconciliation: true,
    viewAuditLog: true,
    viewCompanyWideEconomics: true,
    viewExecutiveRollups: true,
    exportReports: true,
    changeOwnershipPercentages: true,
    runScenarios: true,
    viewCostBreakdown: true,
  },
  ops_finance: {
    viewAllPartnerProjections: false,
    viewOwnProjectionOnly: false,
    editAssumptions: false,
    editProtectedAssumptions: false,
    viewReconciliation: true,
    viewAuditLog: false,
    viewCompanyWideEconomics: true,
    viewExecutiveRollups: false,
    exportReports: true,
    changeOwnershipPercentages: false,
    runScenarios: false,
    viewCostBreakdown: true,
  },
  partner_member: {
    viewAllPartnerProjections: false,
    viewOwnProjectionOnly: true,
    editAssumptions: false,
    editProtectedAssumptions: false,
    viewReconciliation: true,
    viewAuditLog: false,
    viewCompanyWideEconomics: false,
    viewExecutiveRollups: false,
    exportReports: false,
    changeOwnershipPercentages: false,
    runScenarios: false,
    viewCostBreakdown: false,
  },
  limited_ops: {
    viewAllPartnerProjections: false,
    viewOwnProjectionOnly: false,
    editAssumptions: false,
    editProtectedAssumptions: false,
    viewReconciliation: false,
    viewAuditLog: false,
    viewCompanyWideEconomics: false,
    viewExecutiveRollups: false,
    exportReports: false,
    changeOwnershipPercentages: false,
    runScenarios: false,
    viewCostBreakdown: true,
  },
};

export function getFIERole(role: UserRole): FIERole | null {
  return ROLE_TO_FIE_ROLE[role];
}

export function getFIEPermissions(role: UserRole): FIEPermissions | null {
  const fieRole = getFIERole(role);
  if (!fieRole) return null;
  return FIE_ROLE_PERMISSIONS[fieRole];
}

export function hasFIEAccess(role: UserRole): boolean {
  return getFIERole(role) !== null;
}

export function canViewFIERoute(role: UserRole, route: string): boolean {
  const perms = getFIEPermissions(role);
  if (!perms) return false;
  
  switch (route) {
    case "/fie/forecast": return perms.viewCompanyWideEconomics || perms.viewCostBreakdown;
    case "/fie/distributions": return perms.viewAllPartnerProjections || perms.viewOwnProjectionOnly;
    case "/fie/assumptions": return perms.editAssumptions || perms.viewCostBreakdown;
    case "/fie/reconciliation": return perms.viewReconciliation;
    case "/fie/audit": return perms.viewAuditLog;
    default: return false;
  }
}
